<template lang="pug">
div
  .yz-heading-8.text-neutral-700 Supplier details
  supplier-detail-card.mt-3(v-if="selectedSupplier" :supplier="selectedSupplier" :company-id="companyId" :disabled="disabled" v-bind="deleteDescription ? { description: deleteDescription } : {}" @deleted="onSupplierDeleted" @updated="onSupplierUpdated")
  div(v-else)
    .yz-heading-9.text-neutral-700.mb-1.mt-4 Supplier
    template(v-if="!disabled")
      .d-flex.align-center
        yz-api-dropdown(v-model="dropdownValue" placeholder="Choose existing supplier" :items="supplierItems" item-text="value" item-value="id" :loading="isLoading" :error="hasError" :count="totalCount" :page-size="pageSize" :page.sync="currentPage" clearable searchable attach @opened="onOpened" @searched="onSearched")
        .yz-heading-9.px-4 Or
        yz-btn(label="Create new supplier" outlined size="lg" @click="showAddDialog = true")
    .yz-regular-body-sm.text-neutral-500.mt-2(v-else) No supplier assigned
  add-new-supplier-dialog(:open.sync="showAddDialog" @created="onSupplierCreated")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import SupplierDetailCard from './SupplierDetailCard.vue'
import AddNewSupplierDialog from './AddNewSupplierDialog.vue'

export default {
  components: {
    SupplierDetailCard,
    AddNewSupplierDialog
  },
  props: {
    value: {
      type: [Number, String, Object],
      default: null
    },
    companyId: {
      type: [Number, String],
      required: true
    },
    deleteDescription: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showAddDialog: false,
      dropdownValue: null,
      supplierItems: [],
      supplierList: [],
      isLoading: false,
      hasError: false,
      totalCount: 0,
      pageSize: 5,
      currentPage: 1,
      searchQuery: ''
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    selectedSupplier() {
      if (!this.value) return null
      const id = typeof this.value === 'object' ? this.value.id : this.value
      return this.supplierList.find(s => Number(s.id) === Number(id)) || null
    }
  },
  watch: {
    currentPage() {
      if (this.supplierItems.length > 0) {
        this.fetchSuppliers()
      }
    },
    dropdownValue(val) {
      if (val && typeof val === 'object') {
        this.mergeIntoSupplierList([val])
        this.$emit('input', Number(val.id))
      } else {
        this.$emit('input', null)
      }
    },
    value: {
      immediate: true,
      handler(val) {
        if (!val) return
        if (typeof val === 'object') {
          this.mergeIntoSupplierList([val])
        } else if (!this.selectedSupplier) {
          this.fetchSupplierById(val)
        }
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchSuppliers() {
      this.isLoading = true
      this.hasError = false
      try {
        const params = {
          company: this.companyId,
          field: 'medication.sold_by',
          enabled: true,
          page_size: this.pageSize,
          page: this.currentPage
        }
        if (this.searchQuery) {
          params.search = this.searchQuery
        }
        const resp = await this.$axios.get('/options', { params })
        this.supplierItems = resp.data?.results || []
        this.totalCount = resp.data?.count || 0
        this.mergeIntoSupplierList(this.supplierItems)
      } catch {
        this.hasError = true
        this.supplierItems = []
        this.totalCount = 0
      } finally {
        this.isLoading = false
      }
    },
    async fetchSupplierById(id) {
      try {
        const resp = await this.$axios.get(`/options/${id}`)
        if (resp.data) {
          this.mergeIntoSupplierList([resp.data])
        }
      } catch {
        // Silently fail — supplier may have been deleted
      }
    },
    mergeIntoSupplierList(items) {
      items.forEach(item => {
        const index = this.supplierList.findIndex(s => Number(s.id) === Number(item.id))
        if (index >= 0) {
          this.$set(this.supplierList, index, item)
        } else {
          this.supplierList.push(item)
        }
      })
    },
    onOpened() {
      this.searchQuery = ''
      this.currentPage = 1
      this.fetchSuppliers()
    },
    onSearched(term) {
      this.searchQuery = term
      this.currentPage = 1
      this.fetchSuppliers()
    },
    onSupplierCreated(data) {
      this.mergeIntoSupplierList([data])
      this.$emit('input', Number(data.id))
      this.$emit('supplier-created', data)
    },
    onSupplierDeleted(supplierId) {
      this.supplierList = this.supplierList.filter(s => Number(s.id) !== Number(supplierId))
      this.dropdownValue = null
      this.$emit('input', null)
      this.$emit('supplier-deleted', supplierId)
    },
    onSupplierUpdated() {
      if (this.value) {
        const id = typeof this.value === 'object' ? this.value.id : this.value
        this.fetchSupplierById(id)
      }
      this.$emit('supplier-updated')
    }
  }
}
</script>
